import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Route, Routes, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import AppLayout from "@/components/AppLayout";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import JobPositionsPage from "@/pages/JobPositionsPage";
import PositionDetailPage from "@/pages/PositionDetailPage";
import ApplicationsPage from "@/pages/ApplicationsPage";
import ApplicationDetailPage from "@/pages/ApplicationDetailPage";

import SettingsPage from "@/pages/SettingsPage";
import ProfilePage from "@/pages/ProfilePage";
import SupportReportPage from "@/pages/SupportReportPage";
import NotFound from "@/pages/NotFound";
import PublicPostingPage from "@/pages/PublicPostingPage";
import InvitationPage from "@/pages/InvitationPage";
import AdminLayout from "@/components/admin/AdminLayout";
import { SudoGuard } from "@/components/admin/SudoGuard";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminCustomersPage from "@/pages/admin/AdminCustomersPage";
import AdminCustomerDetailPage from "@/pages/admin/AdminCustomerDetailPage";
import AdminSubscriptionsPage from "@/pages/admin/AdminSubscriptionsPage";
import AdminPostingsPage from "@/pages/admin/AdminPostingsPage";
import AdminPostingDetailPage from "@/pages/admin/AdminPostingDetailPage";
import AdminApplicationDetailPage from "@/pages/admin/AdminApplicationDetailPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminUserDetailPage from "@/pages/admin/AdminUserDetailPage";
import AdminAuditPage from "@/pages/admin/AdminAuditPage";
import AdminEmailAuditPage from "@/pages/admin/AdminEmailAuditPage";
import AdminSubscriptionDetailPage from "@/pages/admin/AdminSubscriptionDetailPage";
import AdminFinancePage from "@/pages/admin/AdminFinancePage";
import AdminPositionDetailPage from "@/pages/admin/AdminPositionDetailPage";
import AdminPlansPage from "@/pages/admin/AdminPlansPage";
import AdminApplicationsPage from "@/pages/admin/AdminApplicationsPage";
import { Component, type ReactNode } from "react";
import BrandLoadingScreen from "@/components/shared/BrandLoadingScreen";
import MarketingHomePage from "@/pages/MarketingHomePage";
import ContactPage from "@/pages/ContactPage";
import FaqPage from "@/pages/FaqPage";
import LegalDocumentPage from "@/pages/LegalDocumentPage";
import DpaPage from "@/pages/DpaPage";
import { CookieConsentManager } from "@/components/marketing/CookieConsentManager";
import { SentryConsentBridge } from "@/components/SentryConsentBridge";
import { HelmetProvider } from "react-helmet-async";

class InvitationErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', padding: '16px' }}>
          <div style={{ maxWidth: 400, textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
            <h2 style={{ color: '#0f172a', marginBottom: 8 }}>Errore nel caricamento</h2>
            <p style={{ color: '#64748b', fontSize: 14 }}>{this.state.error.message}</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <BrandLoadingScreen />;
  if (!isAuthenticated) {
    const redirect = `${location.pathname}${location.search}`;
    const loginTo =
      redirect && redirect !== '/login'
        ? `/login?redirect=${encodeURIComponent(redirect)}`
        : '/login';
    return <Navigate to={loginTo} replace />;
  }
  return <>{children}</>;
};

/**
 * RedirectAuthenticated — used on /login and /signup when user is already authenticated.
 * Respects ?redirect= query param so that invite links work correctly after auth.
 */
const RedirectAuthenticated = ({ fallback }: { fallback: string }) => {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') ?? fallback;
  return <Navigate to={redirectTo} replace />;
};

/** Public marketing home for guests; authenticated users go to the app. */
const RootEntry = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <BrandLoadingScreen />;
  if (!isAuthenticated) return <MarketingHomePage />;
  return <Navigate to="/dashboard" replace />;
};

const AppRoutes = () => {
  const { isAuthenticated, isLoading } = useAuth();

  return (
    <Routes>
      {/* Marketing + contact — before slug catch-all */}
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/privacy" element={<LegalDocumentPage docId="privacy" />} />
      <Route path="/terms" element={<LegalDocumentPage docId="terms" />} />
      <Route path="/cookies" element={<LegalDocumentPage docId="cookies" />} />
      <Route path="/dpa" element={<DpaPage />} />
      <Route path="/subprocessors" element={<LegalDocumentPage docId="subprocessors" />} />

      {/* Public candidate apply routes — no auth required, render immediately */}
      <Route path="/apply/:companySlug/:postingSlug" element={<PublicPostingPage />} />
      <Route path="/:companySlug/:postingSlug" element={<PublicPostingPage />} />

      {/* Public invitation route — /i/ prefix avoids conflict with /:companySlug/:postingSlug */}
      <Route path="/i/:invitationId/:token" element={<InvitationErrorBoundary><InvitationPage /></InvitationErrorBoundary>} />

      {/*
        Login must stay mounted during Google popup + POST /auth/session.
        Gating on `isLoading` unmounts LoginPage while loginWithGoogle runs
        (same class of bug fixed on /signup in App.tsx comments below).
      */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <RedirectAuthenticated fallback="/dashboard" />
          ) : (
            <LoginPage />
          )
        }
      />
      {/*
        Signup must stay mounted across the post-credentials flow so the
        account-type chooser (SME vs Agency) can render. The component
        decides internally whether to redirect (only if the user has a
        completed membership). DO NOT add an `isAuthenticated` guard here.

        IMPORTANT: do NOT gate this route on `isLoading` either. The
        signup() call in AuthContext flips isLoading=true while it talks
        to Firebase + /auth/session — if the route renders `null` during
        that window, SignupPage gets unmounted, the local `step` state
        is lost, and when isLoading flips back to false the page mounts
        fresh on step='credentials' (instead of advancing to
        'accountType'). SignupPage already shows an inline loader via
        the `isLoading` flag from useAuth(), so a global gate here is
        redundant and breaks the wizard.
      */}
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />

      <Route path="/" element={<RootEntry />} />

      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="job-positions" element={<JobPositionsPage />} />
        <Route path="job-positions/:posId" element={<PositionDetailPage />} />
        <Route path="job-positions/:posId/applications/:appId" element={<ApplicationDetailPage />} />
        <Route path="postings" element={<Navigate to="/job-positions" replace />} />
        <Route path="applications" element={<ApplicationsPage />} />
        <Route path="applications/:appId" element={<ApplicationDetailPage />} />
        <Route path="team" element={<Navigate to="/settings" replace />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="support" element={<SupportReportPage />} />
      </Route>

      {/*
        Backoffice MVP — Plan BO-01. Mounted as a sibling of `/` because
        the admin shell (AdminLayout) is intentionally separate from the
        recruiter AppLayout (different sidebar, no company switcher,
        admin badge in header). The `<SudoGuard>` checks the Firebase
        custom claim `sudo: true` and silently redirects unauthorized
        users to `/dashboard`.
      */}
      <Route path="/admin" element={<SudoGuard><AdminLayout /></SudoGuard>}>
        <Route index element={<AdminDashboardPage />} />
        <Route path="customers" element={<AdminCustomersPage />} />
        <Route path="customers/:companyId" element={<AdminCustomerDetailPage />} />
        <Route
          path="customers/:companyId/positions/:posId/postings/:postingId"
          element={<AdminPostingDetailPage />}
        />
        <Route
          path="customers/:companyId/positions/:posId/applications/:appId"
          element={<AdminApplicationDetailPage />}
        />
        <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
        <Route path="postings" element={<AdminPostingsPage />} />
        <Route path="applications" element={<AdminApplicationsPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="users/:uid" element={<AdminUserDetailPage />} />
        <Route path="audit" element={<AdminAuditPage />} />
        <Route path="email-audit" element={<AdminEmailAuditPage />} />
        <Route path="customers/:companyId/subscription" element={<AdminSubscriptionDetailPage />} />
        <Route path="customers/:companyId/positions/:posId" element={<AdminPositionDetailPage />} />
        <Route path="finance" element={<AdminFinancePage />} />
        <Route path="plans" element={<AdminPlansPage />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// App root
const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <HelmetProvider>
            <BrowserRouter>
              <SentryConsentBridge />
              <AppRoutes />
              <CookieConsentManager />
            </BrowserRouter>
          </HelmetProvider>
        </TooltipProvider>
      </LanguageProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
